import {Injectable} from '@angular/core';
import {BehaviorSubject, merge, Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, map, shareReplay} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class SpinnerService {
  private readonly spinnerCounter = new BehaviorSubject<number>(0);
  private readonly forceEmit = new Subject<void>();

  /**
   * Observable that emits the visibility state of the spinner.
   * - show() uses debounceTime to prevent flicker on quick operations
   * - showImmediately() and hide() bypass the debounce for instant feedback
   *
   * Note: The SpinnerComponent handles zone re-entry via zone.run() in its
   * subscription, so this service doesn't need to manage Angular zones.
   */
  readonly spinnerVisible$ = merge(
    // Debounced path - for regular show() calls (HTTP interceptor)
    this.spinnerCounter.pipe(debounceTime(500)),
    // Immediate path - bypasses debounce when forceEmit fires
    this.forceEmit.pipe(map(() => this.spinnerCounter.value))
  ).pipe(
    map(counter => counter > 0),
    distinctUntilChanged(),
    shareReplay({bufferSize: 1, refCount: true})
  );

  /**
   * Shows the spinner with 500ms debounce.
   * Use for automatic operations (HTTP interceptor) where quick operations
   * should NOT flash the spinner.
   */
  public show(): void {
    this.spinnerCounter.next(this.spinnerCounter.value + 1);
  }

  /**
   * Shows the spinner immediately without debounce.
   * Use for long-running user-initiated operations where immediate feedback is needed.
   */
  public showImmediately(): void {
    this.spinnerCounter.next(this.spinnerCounter.value + 1);
    this.forceEmit.next();
  }

  /**
   * Hides the spinner immediately (no debounce) when counter reaches 0.
   * Counter is clamped to 0 to prevent negative values.
   * Only triggers forceEmit when fully hidden to avoid showing spinner
   * during overlapping requests.
   */
  public hide(): void {
    const newValue = Math.max(0, this.spinnerCounter.value - 1);
    this.spinnerCounter.next(newValue);
    if (newValue === 0) {
      this.forceEmit.next();
    }
  }
}
